import { useEffect, useState } from "react";

// Best-effort, cosmetic-only: an approximate USD equivalent shown alongside
// the real BTC amount in the payment modal. Never used for pricing or any
// billing decision — the invoice's sat amount (set server-side) is always
// the actual amount owed. Persisted in localStorage (not just in-memory) so
// the rate is only ever re-fetched once every REFRESH_MS, across page loads
// and browser restarts, not once per modal open.
const STORAGE_KEY = "wallet-app:btcUsdRate";
const REFRESH_MS = 3 * 24 * 60 * 60_000; // 3 days

interface StoredRate {
  usd: number;
  fetchedAt: number;
}

function readStoredRate(): StoredRate | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.usd === "number" && typeof parsed?.fetchedAt === "number") return parsed;
    return null;
  } catch {
    return null; // localStorage unavailable (private browsing, blocked storage) — just re-fetch
  }
}

function writeStoredRate(stored: StoredRate) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Best-effort only — next open will just re-fetch instead of reading a persisted value.
  }
}

// In-memory copy of whatever's in localStorage, so multiple modal opens in
// the same tab session never re-read/re-fetch even within the same load.
let memoRate: StoredRate | null | undefined; // undefined = not checked yet this session

export function useBtcUsdRate(): number | null {
  if (memoRate === undefined) memoRate = readStoredRate();
  const [rate, setRate] = useState<number | null>(memoRate?.usd ?? null);

  useEffect(() => {
    if (memoRate && Date.now() - memoRate.fetchedAt < REFRESH_MS) return;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);

    fetch("https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd", {
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const usd = data?.bitcoin?.usd;
        if (typeof usd === "number") {
          const stored = { usd, fetchedAt: Date.now() };
          memoRate = stored;
          writeStoredRate(stored);
          setRate(usd);
        }
      })
      .catch(() => {
        // No rate available — callers just omit the USD line, BTC amount is always shown.
      })
      .finally(() => clearTimeout(timeout));

    return () => controller.abort();
  }, []);

  return rate;
}
